interface VersionRequest {
  imageVersion: number;
  jwtToken: string;
}

interface FormVersionRequest extends VersionRequest {
  formID: string;
  merchantName: string;
}

interface SettingsVersionRequest extends VersionRequest {
  merchantName: string;
}

/**
 * What an admin is told when the version could not be saved. By this point the
 * image service has already overwritten the stored object, so the record's old
 * version token keeps serving donors the cached previous image. Re-uploading
 * and saving runs the upload and this PATCH again.
 */
export const VERSION_NOT_SAVED_MESSAGE =
  'The new image was uploaded, but donors may keep seeing the previous one. Re-upload the image and save again to fix this.';

/** Waits before the 2nd and 3rd attempts. The PATCH sets a fixed value, so a repeat is idempotent. */
export const VERSION_RETRY_DELAYS_MS = [500, 1500];

const isRetryableStatus = (status: number | undefined) =>
  typeof status === 'number' && (status >= 500 || status === 429);

const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * The upload and this PATCH are two requests to two services, so they cannot
 * be atomic from here; making them atomic needs the image service to write the
 * version itself. What the client can do is not give up on a transient failure
 * (network error, 5xx, 429) — bounded, never on a 4xx — and, when it does give
 * up, say so in words an admin can act on.
 */
async function patchVersion(url: string, jwtToken: string, body: object): Promise<void> {
  let lastError: unknown;
  for (let attempt = 0; attempt <= VERSION_RETRY_DELAYS_MS.length; attempt += 1) {
    if (attempt > 0) await wait(VERSION_RETRY_DELAYS_MS[attempt - 1]);

    let response: Response;
    try {
      response = await fetch(url, {
        method: 'PATCH',
        headers: {
          accept: '*/*',
          'content-type': 'application/json',
          Authorization: jwtToken,
        },
        body: JSON.stringify(body),
      });
    } catch (networkError) {
      lastError = networkError;
      continue;
    }

    if (response.ok) return;

    const payload = await response.json().catch(() => ({}));
    const detail = payload?.error ? ` (${payload.error})` : '';
    lastError = new Error(`${VERSION_NOT_SAVED_MESSAGE}${detail}`);
    if (!isRetryableStatus(response.status)) throw lastError;
  }

  if (lastError instanceof Error && lastError.message.startsWith(VERSION_NOT_SAVED_MESSAGE)) {
    throw lastError;
  }
  throw new Error(VERSION_NOT_SAVED_MESSAGE, { cause: lastError });
}

export function persistFormImageVersion({
  formID,
  merchantName,
  imageVersion,
  jwtToken,
}: FormVersionRequest): Promise<void> {
  return patchVersion(import.meta.env.VITE_APP_FORMS_URL, jwtToken, {
    requestType: 'IMAGE_VERSION',
    formID,
    merchantName,
    imageVersion,
  });
}

export function persistSettingsLogoVersion({
  imageVersion,
  jwtToken,
  merchantName,
}: SettingsVersionRequest): Promise<void> {
  return patchVersion(`${import.meta.env.VITE_APP_ACCOUNTS_BACKEND}/nonprofit-settings`, jwtToken, {
    requestType: 'LOGO_VERSION',
    merchantName,
    logoVersion: imageVersion,
  });
}

export function persistOnboardingLogoVersion({
  imageVersion,
  jwtToken,
}: VersionRequest): Promise<void> {
  const baseUrl = import.meta.env.VITE_APP_ACCOUNTS_BACKEND;
  return patchVersion(`${baseUrl}/onboarding/state`, jwtToken, {
    logoVersion: imageVersion,
  });
}
